'use client';

import { useRouter } from 'next/navigation';
import { useState, useTransition, type FormEvent } from 'react';

import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { blogListHref, type BlogSort } from './blog-list-state';

/**
 * Figma „Search“ (248:14822 heading row, mobile 309:17503): 48 px, radius 12, magnifier 20 px
 * #8F978E, Body/M with the #5D655D placeholder (16 px on phones – iOS zooms into smaller inputs).
 * Submits `?search=` to the same list (the sort stays, the page resets); an empty field returns to
 * the whole list. Without JavaScript it is a plain GET form.
 */
export function BlogSearch({
  action,
  value,
  sort,
  label,
  placeholder,
  className,
}: {
  /** Path of the list (the blog or the blog category). */
  action: string;
  value: string;
  sort: BlogSort;
  /** Accessible name of the field (core „Vyhľadajte článok“). */
  label: string;
  placeholder: string;
  className?: string;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [query, setQuery] = useState(value);
  // A new URL value (another search, the back button) replaces what was typed; the field keeps focus.
  const [urlValue, setUrlValue] = useState(value);
  if (urlValue !== value) {
    setUrlValue(value);
    setQuery(value);
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startTransition(() =>
      router.push(blogListHref(action, { search: query.trim(), sort }), { scroll: false }),
    );
  };

  return (
    <form
      role="search"
      action={action}
      method="get"
      onSubmit={onSubmit}
      aria-busy={isPending || undefined}
      className={cn(
        'flex h-12 items-center gap-2.5 rounded-xl border border-[#E2E5DF] bg-[#FFFFFF] px-3.5 transition-colors focus-within:border-[#127A26] focus-within:shadow-focus md:px-4',
        className,
      )}
    >
      <Icon name="magnifying-glass" size={20} className="shrink-0 text-[#8F978E]" />
      <input
        type="search"
        name="search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        enterKeyHint="search"
        maxLength={100}
        className="nt-body-m min-w-0 flex-1 text-ellipsis bg-transparent text-[#0D110D] outline-none placeholder:text-[#5D655D] max-md:text-base"
      />
      {sort === 'most-read' ? <input type="hidden" name="sort" value={sort} /> : null}
    </form>
  );
}
